import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { ModalRef } from '@shared/models/modal-ref';
import { RichEditorService } from '@shared/modules/rich-editor/services/rich-editor.service';
import { DialogFactoryService } from '@shared/services/dialog-factory.service';
import { RichEdit } from 'devexpress-richedit';
import { SecurityReviewUpdate } from '../../abstractions/security-review-update';
import { SecurityTaxResultItemForm } from '../../models/edit-tax-result-approval/security-tax-result-item-form';
import { ExplanationEditorComponent } from '../explanation-editor/explanation-editor.component';

@Component({
  standalone: false,
  selector: 'gstar-explanation-field',
  templateUrl: './explanation-field.component.html',
  styleUrls: ['./explanation-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExplanationFieldComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  securityTaxResultItemForm = input.required<SecurityTaxResultItemForm>();
  isDisabled = input<boolean>(false);
  isInvalid = signal<boolean>(false);
  title = input.required<string>();

  update = output<SecurityReviewUpdate>();
  isShow = signal(false);

  modalEditExplanationsRef?: ModalRef<ExplanationEditorComponent>;

  explanation = computed(() => {
    return this.securityTaxResultItemForm().explanation!;
  });

  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly richEditorService = inject(RichEditorService);
  private readonly dialogFactoryService = inject(DialogFactoryService);

  ngOnInit(): void {
    this.explanation().previousValue = this.explanation().value();
    if (!this.isDisabled()) {
      this.updateValue();
    }
  }

  ngAfterViewInit(): void {
    this.richEditorService.richEditor$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isInvalid.set(this.richEditorService.isEmpty);
      });
  }

  ngOnDestroy(): void {
    if (this.modalEditExplanationsRef) {
      this.modalEditExplanationsRef.close();
    }
  }

  explanationEditorHandler(): void {
    this.modalEditExplanationsRef = this.dialogFactoryService.open(
      this.translateService.instant('editTaxResultList.editExplanation'),
      ExplanationEditorComponent,
      {
        data: {
          content: this.securityTaxResultItemForm().explanation?.value(),
        },
        disableConfirm: this.isInvalid(),
      }
    );

    this.modalEditExplanationsRef
      .afterConfirm<RichEdit | undefined>()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(richEditor => {
        if (richEditor) {
          this.richEditorService.exportToHtml(richEditor, content => {
            this.securityTaxResultItemForm().explanation?.value.set(content);
            this.updateValue();
          });
        }
      });
  }

  updateValue(): void {
    if (this.isDisabled()) {
      return;
    }

    this.update.emit({
      field: 'explanation',
      metadata: this.explanation().metadata,
      securityTaxResultItem: this.securityTaxResultItemForm(),
    });
  }
}
